import React, {
    useState,
    useEffect,
    useRef,
    ForwardedRef,
    useCallback,
    useMemo,
    memo,
} from 'react'
import { Typography, Tooltip, TypographyProps } from '@mui/material'

interface TruncatedTextProps extends TypographyProps {
    text?: string | null
    auto?: boolean
    children?: React.ReactNode
    tooltipTitle?: string
}

const TruncatedText = React.forwardRef<HTMLDivElement, TruncatedTextProps>(
    (
        { children, text, auto = false, tooltipTitle, ...props },
        ref: ForwardedRef<HTMLDivElement>,
    ) => {
        const [hasTooltip, setHasTooltip] = useState(false)
        const textRef = useRef<HTMLDivElement | null>(null)

        const content = useMemo(() => text ?? children ?? '', [text, children])

        /**
         * UBQ-5762: recompute overflow whenever the element's box changes, not
         * just when the text changes. Browser zoom and container resizes alter
         * scrollWidth/clientWidth without touching text/children, so a one-shot
         * measurement went stale — truncated text showed the ellipsis but never
         * gained (or wrongly kept) its hover tooltip. A ResizeObserver on the
         * element plus a window resize listener keeps the flag in sync.
         */
        useEffect(() => {
            const el = textRef.current
            if (!el) return undefined
            const measure = () => setHasTooltip(el.scrollWidth > el.clientWidth)
            measure()
            const observer = new ResizeObserver(measure)
            observer.observe(el)
            window.addEventListener('resize', measure)
            return () => {
                observer.disconnect()
                window.removeEventListener('resize', measure)
            }
        }, [content])

        const setRefs = useCallback(
            (node: HTMLDivElement | null) => {
                textRef.current = node
                if (typeof ref === 'function') {
                    ref(node)
                } else if (ref) {
                    // Safely assign to mutable ref
                    ;(
                        ref as React.MutableRefObject<HTMLDivElement | null>
                    ).current = node
                }
            },
            [ref],
        )

        const commonSx = useMemo(
            () => ({
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...props.sx,
            }),
            [props.sx],
        )

        const showTooltip = hasTooltip || auto

        /**
         * Always wrap in Tooltip and toggle its listeners instead of swapping
         * between wrapped/unwrapped trees. That keeps the Typography DOM node
         * stable, so the ResizeObserver above stays attached across
         * measurements (a re-mount would silently detach it).
         */
        return (
            <Tooltip
                title={tooltipTitle || String(content)}
                placement="top"
                arrow
                disableHoverListener={!showTooltip}
                disableFocusListener={!showTooltip}
                disableTouchListener={!showTooltip}
            >
                <Typography
                    {...props}
                    ref={setRefs}
                    component="div"
                    sx={commonSx}
                >
                    {content}
                </Typography>
            </Tooltip>
        )
    },
)

TruncatedText.displayName = 'TruncatedText'

const MemoizedTruncatedText = memo(TruncatedText)

export default MemoizedTruncatedText
